import classNames from 'classnames';
import { Icon } from '../Icon/Icon';
import { BadgeDate } from './BadgeDate';

type MediaTypes = 'event' | 'quiz' | 'podcast';

export type SizeVariant = 'xl' | 'lg' | 'mdw' | 'md' | 'sm' | 'xs';

export type Props = {
  title?: string;
  text?: string;
  url?: string;
  image?: string;
  srcset?: string;
  badge?: { day: string; month: string };
  datetime?: string;
  location?: string;
  sizeVariant?: SizeVariant;
  sizeVariantClass?: string;
  mediaType?: MediaTypes;
  isHighlight?: boolean;
  highlightColorText?: string;
  highlightColorBackground?: string;
  highlightCallToActionColorText?: string;
  highlightCallToActionColorBackground?: string;
  podcastBackground?: string;
  sizes?: string;
  isSingleDemo?: boolean;
  isTest: boolean;
};

// Not all sizes can be highlighted
const canHighlightSize = (size: SizeVariant) => {
  const highlightableSizes = ['xl', 'sm'];
  return highlightableSizes.includes(size);
};

export const getAttributes = (
  isSingleDemo: boolean,
  sizeVariant: SizeVariant,
  sizeVariantClass: string,
  mediaType: string | undefined,
  isHighlight: boolean,
  highlightColorText: string,
  highlightColorBackground: string,
  highlightCallToActionColorText: string,
  highlightCallToActionColorBackground: string,
) => ({
  className: classNames(
    'bo-media-card',
    `bo-media-card--${mediaType}`,
    isSingleDemo ? `bo-media-card--${sizeVariant}` : sizeVariantClass,
    {
      'bo-media-card--highlight': isHighlight && canHighlightSize(sizeVariant),
    },
    `${isSingleDemo ? 'bo-media-card--xl-@l' : 'bo-media-card--sm bo-media-card--md-@l'}`,
  ),
  style: {
    '--background-color': highlightColorBackground,
    '--color': highlightColorText,
    '--call-to-action-color': highlightCallToActionColorText,
    '--call-to-action-background': highlightCallToActionColorBackground,
  },
});

export const MediaCard = ({
  title,
  text,
  url,
  image,
  srcset,
  badge = { day: '1', month: '1' },
  datetime,
  location,
  sizeVariant = 'sm',
  sizeVariantClass = '',
  mediaType,
  isHighlight = true,
  highlightColorText = '',
  highlightColorBackground = '',
  highlightCallToActionColorText = '',
  highlightCallToActionColorBackground = '',
  isSingleDemo = false,
  isTest = false,
}: Props) => {
  const attributes = getAttributes(
    isSingleDemo,
    sizeVariant,
    sizeVariantClass,
    mediaType,
    isHighlight,
    highlightColorText,
    highlightColorBackground,
    highlightCallToActionColorText,
    highlightCallToActionColorBackground,
  );

  return (
    <article {...attributes}>
      <a href={url} title={title} className="bo-media-card__link"></a>
      <figure className="bo-media-card__media">
        <img
          loading="lazy"
          decoding="async"
          className="bo-media-card__media__img"
          src={image}
          srcSet={srcset}
          alt="Image Alt Text"
        />
      </figure>
      <div className="bo-media-card__details">
        {mediaType === 'event' ? (
          <div className="bo-media-card__event-title-wrapper">
            <BadgeDate day={badge.day} month={badge.month} />
            <h3 className="bo-media-card__title u-hyphens">{title}</h3>
          </div>
        ) : (
          <h3 className={`bo-media-card__title u-hyphens`}>{title}</h3>
        )}

        {mediaType === 'event' && (
          <div className="bo-media-card__event-tags">
            <div className="bo-media-card__event-tags__item">
              {!isTest && (
                <Icon
                  iconName="clock"
                  width={16}
                  height={16}
                  wrapperClass="bo-icon bo-icon--size-sm"
                />
              )}

              <span>{datetime}</span>
            </div>
            <div className="bo-media-card__event-tags__item">
              {!isTest && (
                <Icon
                  iconName="pin"
                  width={16}
                  height={16}
                  wrapperClass="bo-icon bo-icon--size-sm"
                />
              )}
              <span>{location}</span>
            </div>
          </div>
        )}

        <p className="bo-media-card__text u-hyphens">{text}</p>
      </div>
    </article>
  );
};
